import React from 'react'
import { Link } from 'react-router-dom'
import { authBaseUrl, authAppId } from './authApiConfig'
import { useTranslation } from '@structura/i18n'

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation()
  const [email, setEmail] = React.useState<string>('')
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)
  const [submitted, setSubmitted] = React.useState<boolean>(false)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setErrorMessage(null)
    setIsSubmitting(true)

    try {
      const response = await fetch(`${authBaseUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, appId: authAppId })
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => undefined)
        const message: string = (payload !== undefined && typeof payload.error === 'string')
          ? payload.error
          : t('auth.forgot.errProcess')
        setErrorMessage(message)
        return
      }

      setSubmitted(true)
    } catch {
      setErrorMessage(t('auth.common.networkError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card border-0 shadow-lg" style={{ borderRadius: '1rem' }}>
              <div className="card-body p-5 text-center">
                <div 
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                  style={{ width: '70px', height: '70px', background: 'rgba(34, 139, 34, 0.1)' }}
                >
                  <i className="bi bi-envelope-check" style={{ fontSize: '2rem', color: 'forestgreen' }}></i>
                </div>
                <h2 className="fw-bold mb-3">{t('auth.forgot.checkEmail')}</h2>
                <p className="text-muted mb-4">
                  {t('auth.forgot.checkEmailBefore')} <strong>{email}</strong>{t('auth.forgot.checkEmailAfter')}
                </p>
                <p className="text-muted small mb-4">
                  {t('auth.forgot.noEmailHint')}
                </p>
                <Link to="/account" className="btn btn-outline-secondary">
                  <i className="bi bi-arrow-left me-2"></i>
                  {t('auth.forgot.backToSignIn')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card border-0 shadow-lg" style={{ borderRadius: '1rem' }}>
            <div className="card-body p-5">
              <div className="text-center mb-4">
                <div 
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ width: '70px', height: '70px', background: 'rgba(34, 139, 34, 0.1)' }}
                >
                  <i className="bi bi-key" style={{ fontSize: '2rem', color: 'forestgreen' }}></i>
                </div>
                <h2 className="fw-bold mb-2">{t('auth.forgot.title')}</h2>
                <p className="text-muted">
                  {t('auth.forgot.subtitle')}
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="forgotEmail" className="form-label">{t('auth.forgot.emailAddress')}</label>
                  <input
                    id="forgotEmail"
                    type="email"
                    className="form-control form-control-lg"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>

                {errorMessage !== null && (
                  <div className="alert alert-danger mb-4" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {errorMessage}
                  </div>
                )}

                <div className="d-grid mb-4">
                  <button 
                    type="submit" 
                    className="btn btn-lg"
                    style={{ background: 'forestgreen', color: 'white' }}
                    disabled={isSubmitting || email.length === 0}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t('auth.forgot.sending')}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-envelope me-2"></i>
                        {t('auth.forgot.sendInstructions')}
                      </>
                    )}
                  </button>
                </div>

                <div className="text-center">
                  <Link to="/account" className="text-decoration-none" style={{ color: 'forestgreen' }}>
                    <i className="bi bi-arrow-left me-1"></i>
                    {t('auth.forgot.backToSignIn')}
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword

